import type {
  Match,
  MatchStatus,
  Team,
  League,
  CompetitionCategory,
  MatchStats,
  Lineup,
  LineupPlayer,
  MatchEvent,
  CommentaryEntry,
} from "@/types/match";
import { colorsFromString } from "@/utils/color";
import { ApiError } from "./errors";
import { ENDPOINTS } from "./endpoints";

// ── Wire types (subset of API-Football's v3 response shape) ───────────────
// Kept close to API-Football's own field names on purpose — anyone reading
// this next to their docs at api-football.com/documentation-v3 should
// recognize it immediately.
interface ApiFootballTeamRef {
  id: number;
  name: string;
  logo: string;
}
interface ApiFootballFixture {
  fixture: {
    id: number;
    date: string; // ISO timestamp
    venue: { name: string | null; city: string | null };
    status: { short: string; elapsed: number | null; extra?: number | null };
  };
  league: { id: number; name: string; country: string; logo: string };
  teams: { home: ApiFootballTeamRef; away: ApiFootballTeamRef };
  goals: { home: number | null; away: number | null };
}
interface ApiFootballLeagueEntry {
  league: { id: number; name: string; type: string; logo: string };
  country: { name: string };
}

async function proxyFetch<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(`Proxy request failed (${res.status}): ${body.slice(0, 200)}`, res.status);
  }
  return res.json() as Promise<T>;
}

// API-Football's fixture status codes → our four-state model. Anything not
// explicitly listed falls back to SCHEDULED rather than throwing, since new
// status codes occasionally get added upstream (e.g. VAR review states).
const STATUS_MAP: Record<string, MatchStatus> = {
  TBD: "SCHEDULED",
  NS: "SCHEDULED",
  "1H": "LIVE",
  "2H": "LIVE",
  ET: "LIVE",
  BT: "LIVE",
  P: "LIVE",
  LIVE: "LIVE",
  SUSP: "LIVE",
  INT: "LIVE",
  HT: "HALFTIME",
  FT: "FULLTIME",
  AET: "FULLTIME",
  PEN: "FULLTIME",
  PST: "POSTPONED",
  CANC: "POSTPONED",
  ABD: "POSTPONED",
  AWD: "POSTPONED",
  WO: "POSTPONED",
};

function abbreviate(name: string): string {
  const words = name.replace(/^(FC|CF|AC|SC)\s|\s(FC|CF|AC|SC)$/gi, "").trim().split(" ");
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

function mapTeam(t: ApiFootballTeamRef): Team {
  const { primary, secondary } = colorsFromString(String(t.id));
  return {
    id: String(t.id),
    name: t.name,
    shortName: abbreviate(t.name),
    crestUrl: t.logo || null, // real crest straight from API-Football — TeamCrest renders it automatically
    primaryColor: primary,
    secondaryColor: secondary,
  };
}

// API-Football's fixture payload doesn't include the league's `type` field
// (that only comes back from /leagues), so competitions are classified here
// by name pattern. It's a heuristic, not authoritative — cross-check against
// fetchLeagues() below if you need it to be exact.
function classifyLeague(name: string, type?: string): CompetitionCategory {
  const n = name.toLowerCase();
  if (/(world cup|euro(?!pa)|copa am[eé]rica|afcon|nations league|gold cup|asian cup|friendl)/.test(n)) {
    return "International";
  }
  if (/(champions league|europa|conference league|libertadores|sudamericana|caf champions|concacaf champions|club world cup)/.test(n)) {
    return "Continental Club";
  }
  if (/(super cup|supercup|supercoppa|community shield|troph[ée])/.test(n)) {
    return "Super Cup";
  }
  return type === "Cup" ? "Domestic Cup" : "Domestic League";
}

function mapLeague(f: ApiFootballFixture["league"]): League {
  return {
    id: String(f.id),
    name: f.name,
    shortName: f.name.length > 18 ? abbreviate(f.name) : f.name,
    country: f.country,
    emblemUrl: f.logo || null,
    category: classifyLeague(f.name),
    accentColor: colorsFromString(String(f.id)).primary,
  };
}

function mapFixtureToMatch(f: ApiFootballFixture): Match {
  const status = STATUS_MAP[f.fixture.status.short] ?? "SCHEDULED";
  return {
    id: String(f.fixture.id),
    league: mapLeague(f.league),
    homeTeam: mapTeam(f.teams.home),
    awayTeam: mapTeam(f.teams.away),
    status,
    kickoff: f.fixture.date,
    minute: f.fixture.status.elapsed,
    minuteExtra: f.fixture.status.extra ?? null,
    homeScore: f.goals.home ?? 0,
    awayScore: f.goals.away ?? 0,
    venue: [f.fixture.venue.name, f.fixture.venue.city].filter(Boolean).join(", ") || "Venue TBC",
    // API-Football is a stats/schedule provider — it has no concept of
    // video. Streams (and any "watch on [broadcaster]" link) are resolved
    // separately; see streamResolver.ts.
    streams: [],
    externalWatchUrl: null,
    // Events/commentary/lineups/stats need their own endpoints
    // (/fixtures/events, /fixtures/lineups, /fixtures/statistics) — wire
    // those in here the same way once you need the side panel tabs fully
    // live; each returns an empty state cleanly in the meantime.
    events: [],
    commentary: [],
    stats: null,
    lineups: null,
  };
}

export async function fetchLiveFixtures(): Promise<Match[]> {
  const data = await proxyFetch<ApiFootballFixture[]>(ENDPOINTS.liveMatches);
  return data.map(mapFixtureToMatch);
}

export async function fetchFixturesByDate(dateStr: string): Promise<Match[]> {
  const data = await proxyFetch<ApiFootballFixture[]>(ENDPOINTS.scheduledMatches(dateStr));
  return data.map(mapFixtureToMatch);
}

export async function fetchFixtureById(id: string): Promise<Match> {
  const data = await proxyFetch<ApiFootballFixture[]>(ENDPOINTS.matchById(id));
  if (!data.length) throw new ApiError("Match not found", 404);
  return mapFixtureToMatch(data[0]);
}

export async function searchFixtures(query: string): Promise<Match[]> {
  const data = await proxyFetch<ApiFootballFixture[]>(ENDPOINTS.search(query));
  return data.map(mapFixtureToMatch);
}

export async function fetchLeagues(): Promise<League[]> {
  const data = await proxyFetch<ApiFootballLeagueEntry[]>(ENDPOINTS.leagues);
  return data.map((entry) => ({
    id: String(entry.league.id),
    name: entry.league.name,
    shortName: entry.league.name.length > 18 ? abbreviate(entry.league.name) : entry.league.name,
    country: entry.country.name,
    emblemUrl: entry.league.logo || null,
    category: classifyLeague(entry.league.name, entry.league.type),
    accentColor: colorsFromString(String(entry.league.id)).primary,
  }));
}

// ── Statistics, lineups, events ─────────────────────────────────────────
// These are separate API-Football endpoints from the base fixture data —
// intentionally fetched separately (see api.ts's getMatchById) rather than
// bundled into every match card, since resolving them costs real upstream
// requests against a tight rate limit and most matches in a list are never
// actually opened.

interface ApiFootballStatEntry {
  type: string;
  value: number | string | null;
}
interface ApiFootballTeamStats {
  team: { id: number };
  statistics: ApiFootballStatEntry[];
}

function parseStatValue(entries: ApiFootballStatEntry[], type: string): number {
  const entry = entries.find((e) => e.type === type);
  if (!entry || entry.value == null) return 0;
  if (typeof entry.value === "number") return entry.value;
  // Percentages (possession) come back as e.g. "55%".
  const parsed = parseInt(String(entry.value).replace("%", ""), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export async function fetchFixtureStatistics(id: string): Promise<MatchStats | null> {
  const data = await proxyFetch<ApiFootballTeamStats[]>(ENDPOINTS.statisticsForMatch(id));
  if (!data || data.length < 2) return null; // stats aren't published for every fixture/competition
  const [home, away] = data;
  const get = (teamStats: ApiFootballTeamStats, type: string) => parseStatValue(teamStats.statistics, type);
  return {
    possession: [get(home, "Ball Possession"), get(away, "Ball Possession")],
    shots: [get(home, "Total Shots"), get(away, "Total Shots")],
    shotsOnTarget: [get(home, "Shots on Goal"), get(away, "Shots on Goal")],
    corners: [get(home, "Corner Kicks"), get(away, "Corner Kicks")],
    fouls: [get(home, "Fouls"), get(away, "Fouls")],
    offsides: [get(home, "Offsides"), get(away, "Offsides")],
  };
}

interface ApiFootballLineupPlayer {
  player: { name: string; number: number; pos: string | null };
}
interface ApiFootballTeamLineup {
  team: { id: number };
  formation: string | null;
  startXI: ApiFootballLineupPlayer[];
  substitutes: ApiFootballLineupPlayer[];
}

function mapLineupPlayers(players: ApiFootballLineupPlayer[]): LineupPlayer[] {
  return players.map((p) => ({
    number: p.player.number,
    name: p.player.name,
    position: p.player.pos ?? "—",
    // API-Football's lineups endpoint doesn't flag the captain directly on
    // this plan — left unset rather than guessing.
  }));
}

export async function fetchFixtureLineups(id: string): Promise<{ home: Lineup; away: Lineup } | null> {
  const data = await proxyFetch<ApiFootballTeamLineup[]>(ENDPOINTS.lineupsForMatch(id));
  if (!data || data.length < 2) return null; // not published until shortly before kickoff
  const [home, away] = data;
  return {
    home: {
      formation: home.formation || "—",
      starters: mapLineupPlayers(home.startXI),
      substitutes: mapLineupPlayers(home.substitutes),
    },
    away: {
      formation: away.formation || "—",
      starters: mapLineupPlayers(away.startXI),
      substitutes: mapLineupPlayers(away.substitutes),
    },
  };
}

interface ApiFootballEvent {
  time: { elapsed: number; extra: number | null };
  team: { id: number };
  player: { name: string };
  type: string; // "Goal" | "Card" | "subst" | "Var"
  detail: string; // "Normal Goal" | "Yellow Card" | "Red Card" | "Substitution 1" | ...
}

function mapEventType(af: ApiFootballEvent): MatchEvent["type"] | null {
  if (af.type === "Goal") return "GOAL";
  if (af.type === "Card" && af.detail === "Yellow Card") return "YELLOW_CARD";
  if (af.type === "Card" && af.detail === "Red Card") return "RED_CARD";
  if (af.type === "subst") return "SUBSTITUTION";
  if (af.type === "Var") return "VAR";
  return null; // ignore event subtypes we don't have a badge/icon for
}

// API-Football has no separate free-text commentary feed on this plan — this
// synthesizes readable commentary lines from the structured events data,
// which is what actually populates the Commentary tab for real matches.
function commentaryText(af: ApiFootballEvent, mappedType: MatchEvent["type"]): string {
  switch (mappedType) {
    case "GOAL":
      return `GOAL! ${af.player.name} finds the net in the ${af.time.elapsed}' minute.`;
    case "YELLOW_CARD":
      return `${af.player.name} is shown a yellow card.`;
    case "RED_CARD":
      return `${af.player.name} is sent off with a red card!`;
    case "SUBSTITUTION":
      return `Substitution: ${af.player.name} comes on.`;
    case "VAR":
      return `VAR review in progress.`;
  }
}

export async function fetchFixtureEvents(id: string): Promise<{ events: MatchEvent[]; commentary: CommentaryEntry[] }> {
  const data = await proxyFetch<ApiFootballEvent[]>(ENDPOINTS.eventsForMatch(id));
  const events: MatchEvent[] = [];
  const commentary: CommentaryEntry[] = [];

  for (const af of data ?? []) {
    const mappedType = mapEventType(af);
    if (!mappedType) continue;
    const minute = af.time.elapsed;
    const eventId = `e-${id}-${minute}-${af.player.name}-${mappedType}`;
    events.push({
      id: eventId,
      minute,
      type: mappedType,
      teamId: String(af.team.id),
      playerName: af.player.name,
      detail: af.detail,
    });
    commentary.push({
      id: `c-${eventId}`,
      minute,
      text: commentaryText(af, mappedType),
      isKeyMoment: mappedType === "GOAL" || mappedType === "RED_CARD",
    });
  }

  // Most recent first, matching how CommentaryTab expects them (and how the
  // mock data is ordered).
  return { events: events.reverse(), commentary: commentary.reverse() };
}
